import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { FormSection } from '@/shared/ui/components/form';
import { Select, Text } from '@/shared/ui';
import { TextInput, CheckboxRaw } from '@/shared/ui/components/form/inputs';
import { cepMask } from '@/shared/utils/input-masks';
import { Field } from './FieldBase';
import { FieldValueAutocomplete } from './FieldValueAutocomplete';
import { CondominiumFields } from './CondominiumFields';
import { AddressUnitFields, DistanceToSeaField } from './AddressUnitFields';
import { useAgencyRequired } from './agency-required-context';
import { BRAZILIAN_STATES, PROPERTY_FIELD_LIMITS } from './schema';
import type { PropertyFormValues } from './schema';

interface AddressSectionProps {
  form: UseFormReturn<PropertyFormValues>;
  onCepBlur: (cep: string) => void;
  agencyId: string;
  /** The property under edit, kept out of the condominium prefill vote. */
  propertyId?: string;
}

/**
 * Address block of the property drawer form. Renders postal code, street,
 * number, complement, neighbourhood, city, state and the condominium flag,
 * masking the postal code and notifying the parent when it is complete.
 *
 * @param {AddressSectionProps} props - Form instance and postal-code callback.
 * @returns {JSX.Element} The rendered address form section.
 */
export function AddressSection({ form, onCepBlur, agencyId, propertyId }: AddressSectionProps) {
  const { t } = useTranslation('features/properties');
  const errors = form.formState.errors;
  const isCondominium = form.watch('condominium');
  const numberRequired = useAgencyRequired('number');

  const handleCepChange = useCallback((value: string, onChange: (v: string) => void) => {
    const { masked, unmasked } = cepMask(value);
    onChange(masked);
    if (unmasked.length === 8) onCepBlur(unmasked);
  }, [onCepBlur]);

  return (
    <FormSection title={t('propertyDrawer.address.title')}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Controller
          name="postal_code"
          control={form.control}
          render={({ field }) => (
            <TextInput
              label="CEP"
              required
              value={field.value}
              error={errors.postal_code?.message}
              placeholder="00.000-000"
              onChange={e => handleCepChange(e.target.value, field.onChange)}
              data-field="postal_code"
            />
          )}
        />
        <Controller
          name="number"
          control={form.control}
          render={({ field }) => (
            <TextInput
              label={t('propertyDrawer.address.number')}
              required={numberRequired}
              value={field.value}
              error={errors.number?.message}
              placeholder="Nº"
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              maxLength={PROPERTY_FIELD_LIMITS.number.maxLength}
            data-field="number"
            />
          )}
        />
      </div>

      <Controller
        name="street"
        control={form.control}
        render={({ field }) => (
          <TextInput
            label={t('propertyDrawer.address.street')}
            required
            value={field.value}
            error={errors.street?.message}
            placeholder={t('propertyDrawer.address.streetPlaceholder')}
            onChange={field.onChange}
            onBlur={field.onBlur}
            name={field.name}
            maxLength={PROPERTY_FIELD_LIMITS.street.maxLength}
            data-field="street"
          />
        )}
      />

      <Controller
        name="complement"
        control={form.control}
        render={({ field }) => (
          <TextInput
            label={t('propertyDrawer.address.complement')}
            value={field.value}
            placeholder={t('propertyDrawer.address.complementPlaceholder')}
            onChange={field.onChange}
            onBlur={field.onBlur}
            name={field.name}
            maxLength={PROPERTY_FIELD_LIMITS.complement.maxLength}
          />
        )}
      />

      <Controller
        name="neighborhood"
        control={form.control}
        render={({ field }) => (
          <FieldValueAutocomplete
            agencyId={agencyId}
            field="neighborhood"
            label={t('propertyDrawer.address.neighborhood')}
            required
            value={field.value}
            error={errors.neighborhood?.message}
            placeholder={t('propertyDrawer.address.neighborhood')}
            onChange={field.onChange}
            maxLength={PROPERTY_FIELD_LIMITS.neighborhood.maxLength}
            data-field="neighborhood"
          />
        )}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-3)' }}>
        <Controller
          name="city"
          control={form.control}
          render={({ field }) => (
            <TextInput
              label={t('propertyDrawer.address.city')}
              required
              value={field.value}
              error={errors.city?.message}
              placeholder={t('propertyDrawer.address.city')}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              maxLength={PROPERTY_FIELD_LIMITS.city.maxLength}
            data-field="city"
            />
          )}
        />
        <Controller
          name="state"
          control={form.control}
          render={({ field }) => (
            <Field label="UF" error={errors.state?.message} required>
              <Select
                data={BRAZILIAN_STATES.map(s => ({ value: s, label: s }))}
                value={field.value || null}
                onChange={(v: string | string[] | null) => {
                  const next = Array.isArray(v) ? v[0] : v;
                  field.onChange(next ?? '');
                }}
                placeholder="UF"
              />
            </Field>
          )}
        />
      </div>

      <DistanceToSeaField form={form} />

      <Controller
        name="condominium"
        control={form.control}
        render={({ field }) => (
          <Text
            as="label"
            className="flex items-center gap-(--space-2) cursor-pointer text-(--text-primary)"
            style={{ fontFamily: 'var(--font-family)' }}
          >
            <CheckboxRaw value={!!field.value} onToggle={v => field.onChange(v)} />
            {t('propertyDrawer.address.condominium')}
          </Text>
        )}
      />
      {isCondominium && <CondominiumFields form={form} agencyId={agencyId} propertyId={propertyId} />}
      <AddressUnitFields form={form} />
    </FormSection>
  );
}
